import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "@bcc/core-renderers/styles.css";
import { ComponentTypes, type All8PageContent } from "@bcc/core-renderers";
import { createRuntimeImageComponent, resolveStorageUrl } from "../../Helper/storage";
import { reorderList } from "../LayoutEditor/utils";
import PageEditorComponentsPanel from "./PageEditorComponentsPanel";
import PageEditorMarkdownPanel from "./PageEditorMarkdownPanel";
import PageEditorJsonPanel from "./PageEditorJsonPanel";
import PageEditorPreviewPanel, { PREVIEW_MAX_WIDTH, PREVIEW_VIEWPORTS, type PreviewViewportKey } from "./PageEditorPreviewPanel";
import PageEditorComponentLibraryModal from "./PageEditorComponentLibraryModal";
import { useComponentLibraryPicker } from "./useComponentLibraryPicker";
import { useComponentHistory } from "./useComponentHistory";
import { wrapComponentInContainer, unwrapContainerAt } from "./containerOps";
import { componentDefinitionMap, createComponentInstance, type ComponentDefinition, type Path } from "../../Pages/pageComponentLibrary";
import { componentsForPayload, getComponentRuntimeKey, normalizeComponents, parsePagePayload, serializePagePayload, setValueAtPath } from "./pageEditorUtils";
import { duplicateComponentAt } from "./componentListUtils";
import { pageToEditableMarkdown } from "./pageToEditableMarkdown";
import { editableMarkdownToPage } from "./editableMarkdownToPage";

type ContentViewMode = "blocks" | "markdown" | "json";

type EmbeddedComponentsEditorProps = {
	label?: string;
	helper?: string;
	value?: any[] | null;
	onChange: (next: any[]) => void;
	disabled?: boolean;
	/** Initial state of the live preview: true on full-page editing, false inside modals (cramped + heavy). */
	defaultShowPreview?: boolean;
};

const DEFAULT_LANGUAGE = "fr";
const MARKDOWN_PREVIEW_DEBOUNCE_MS = 800;

/** The markdown/JSON serializers operate on a whole AdminPage; wrap the embedded component list
 *  in a throwaway page shell so they can be reused unchanged. Slug/metadata here are never saved. */
const buildDraft = (components: All8PageContent, label?: string, description?: string) => ({
	slug: "",
	language: DEFAULT_LANGUAGE,
	title: label ?? "",
	metadata: { title: label ?? "", description: description ?? "" },
	components
});

/** Pill toggle for the Blocks / Markdown / JSON editing modes. */
const ModeButton: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({
	active,
	onClick,
	children
}) => (
	<button
		type="button"
		onClick={onClick}
		className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
			active
				? "border border-slate-800 bg-slate-800 text-white shadow-sm"
				: "border border-slate-200 bg-white text-slate-700 hover:border-slate-300"
		}`}
	>
		{children}
	</button>
);

/**
 * Embedded flavor of the page editor: edits an inline list of page components stored inside
 * another document's field (records, singletons) rather than a standalone page. Same three
 * authoring modes as the full editor : block forms, editable Markdown, raw JSON : plus a live
 * preview. The component list is held in local state and committed to the parent through
 * `onChange` as a key-less payload: runtime keys are regenerated on every normalize and must
 * never reach persisted JSON.
 */
const EmbeddedComponentsEditor: React.FC<EmbeddedComponentsEditorProps> = ({
	label = "Components",
	helper,
	value,
	onChange,
	disabled = false,
	defaultShowPreview = true
}) => {
	const [contentViewMode, setContentViewMode] = useState<ContentViewMode>("blocks");
	// The live preview re-renders the whole component tree on every keystroke : heavy inside a
	// form modal, where it starts hidden (defaultShowPreview=false); the header checkbox toggles it.
	const [showPreview, setShowPreview] = useState(defaultShowPreview);
	const [activeComponentKey, setActiveComponentKey] = useState<string | null>(null);
	const [showLibrary, setShowLibrary] = useState(false);
	const [pageJsonDraft, setPageJsonDraft] = useState("");
	const [pageJsonError, setPageJsonError] = useState<string | null>(null);
	const [markdownDraft, setMarkdownDraft] = useState("");
	const [markdownError, setMarkdownError] = useState<string | null>(null);
	const [previewViewport, setPreviewViewport] = useState<PreviewViewportKey>("full");
	const [previewContainerWidth, setPreviewContainerWidth] = useState<number | null>(null);
	const previewContainerRef = useRef<HTMLDivElement | null>(null);
	const viewModeRef = useRef<ContentViewMode>(contentViewMode);
	const lastMarkdownKeyRef = useRef<string>("");
	const lastJsonKeyRef = useRef<string>("");
	const markdownDraftRef = useRef<string>("");
	const markdownPreviewDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const markdownInsertRef = useRef<((def: ComponentDefinition) => void) | null>(null);

	const normalizedValue = useMemo(() => normalizeComponents(value), [value]);
	const [componentsState, setComponentsState] = useState<All8PageContent>(normalizedValue);

	// Payload of the last state WE committed. Lets the resync effect tell our own
	// round-trip (onChange → new value prop) apart from an EXTERNAL replacement
	// (a different document loaded), which must clear the undo/redo history.
	const committedKeyRef = useRef<string>(JSON.stringify(componentsForPayload(normalizedValue)));
	const [historyEpoch, setHistoryEpoch] = useState(0);

	useEffect(() => {
		const key = JSON.stringify(componentsForPayload(normalizedValue));
		if (key !== committedKeyRef.current) {
			// External replacement (initial mount or a new document): reset history.
			committedKeyRef.current = key;
			setHistoryEpoch((e) => e + 1);
		}
		setComponentsState(normalizedValue);
	}, [normalizedValue]);

	const commitComponents = useCallback(
		(next: All8PageContent) => {
			const payload = componentsForPayload(next);
			committedKeyRef.current = JSON.stringify(payload);
			setComponentsState(next);
			onChange(payload);
		},
		[onChange]
	);

	const previewComponents = useMemo<All8PageContent>(() => normalizeComponents(componentsState), [componentsState]);
	// Index of the card currently open in the blocks panel (runtime keys are index-derived).
	const activeComponentIndex = useMemo(() => {
		if (!activeComponentKey) return null;
		const index = previewComponents.findIndex(
			(component, i) => getComponentRuntimeKey(component.componentType as string, i) === activeComponentKey
		);
		return index >= 0 ? index : null;
	}, [activeComponentKey, previewComponents]);

	// Preview → card linking: clicking a block in the iframe opens its card (UI state only).
	const handleSelectComponentFromPreview = useCallback((index: number) => {
		const component = previewComponents[index];
		if (!component) return;
		setActiveComponentKey(getComponentRuntimeKey(component.componentType as string, index));
	}, [previewComponents]);
	// DynamicComponents is a template-only injector : never offered here.
	const excludeDynamic = useCallback(
		(definition: ComponentDefinition) => definition.type === ComponentTypes.DynamicComponents,
		[]
	);
	const libraryPicker = useComponentLibraryPicker({ exclude: excludeDynamic });

	const runtimeImageComponent = useMemo(() => createRuntimeImageComponent(), []);
	const runtimeConfig = useMemo(
		() => ({
			disableLinks: true,
			imageComponent: runtimeImageComponent,
			// La Galerie (et tout bloc à <img> brut) résout ses clés bucket ici : // sans quoi l'aperçu en direct affiche des images cassées.
			assetResolver: resolveStorageUrl
		}),
		[runtimeImageComponent]
	);
	const previewWidth = PREVIEW_VIEWPORTS[previewViewport];

	const previewCss = `
    .page-editor-preview {
      max-width: ${PREVIEW_MAX_WIDTH}px;
      margin-left: auto;
      margin-right: auto;
      width: 100%;
    }
    .page-editor-preview section {
      margin-left: 0 !important;
      margin-right: 0 !important;
      max-width: ${PREVIEW_MAX_WIDTH}px !important;
    }
    .page-editor-preview section[class*="mx-[calc(50%-50vw)"] {
      margin-left: auto !important;
      margin-right: auto !important;
      max-width: ${PREVIEW_MAX_WIDTH}px !important;
      width: 100% !important;
    }
    .page-editor-preview section .max-w-7xl {
      max-width: ${PREVIEW_MAX_WIDTH}px;
    }
  `;

	// Measure the preview column so the scaled preview can compute its zoom factor. Re-runs on
	// viewport toggle because the layout jumps; the double requestAnimationFrame takes a second
	// measurement after the browser has actually painted the new layout (a single rAF can still
	// observe the pre-toggle width). ResizeObserver covers ordinary window/panel resizes.
	useEffect(() => {
		if (!showPreview) return;
		const el = previewContainerRef.current;
		if (!el) return;
		const updateWidth = () => {
			const w = el.getBoundingClientRect().width;
			if (w > 0) setPreviewContainerWidth(w);
		};
		updateWidth();
		const observer = new ResizeObserver((entries) => {
			const entry = entries[0];
			const w = entry?.contentRect?.width ?? 0;
			if (w > 0) setPreviewContainerWidth(w);
		});
		observer.observe(el);
		const raf = requestAnimationFrame(() => {
			requestAnimationFrame(updateWidth);
		});
		return () => {
			observer.disconnect();
			cancelAnimationFrame(raf);
		};
	}, [previewViewport, showPreview]);

	const updateComponentValue = useCallback(
		(index: number, path: Path, value: any) => {
			if (disabled) return;
			const components = normalizeComponents(componentsState);
			if (!components[index]) return;
			const updatedComponent = setValueAtPath(components[index], path, value);
			const nextComponents = [...components];
			nextComponents[index] = updatedComponent;
			commitComponents(nextComponents);
		},
		[commitComponents, componentsState, disabled]
	);

	const removeComponent = useCallback(
		(index: number) => {
			if (disabled) return;
			const components = normalizeComponents(componentsState);
			const nextComponents = components.filter((_, i) => i !== index);
			commitComponents(nextComponents);
		},
		[commitComponents, componentsState, disabled]
	);

	const moveComponent = useCallback(
		(from: number, to: number) => {
			if (disabled) return;
			const components = normalizeComponents(componentsState);
			const nextComponents = reorderList(components, from, to);
			commitComponents(nextComponents);
		},
		[commitComponents, componentsState, disabled]
	);

	const duplicateComponent = useCallback(
		(index: number) => {
			if (disabled) return;
			const components = normalizeComponents(componentsState);
			const nextComponents = duplicateComponentAt(components, index);
			if (!nextComponents) return;
			commitComponents(normalizeComponents(nextComponents));
			// The clone lands right below the original; open it (runtime keys are index-derived).
			setActiveComponentKey(getComponentRuntimeKey(components[index].componentType as string, index + 1));
		},
		[commitComponents, componentsState, disabled]
	);

	const wrapInContainer = useCallback(
		(index: number) => {
			if (disabled) return;
			const components = normalizeComponents(componentsState);
			const next = wrapComponentInContainer(components, index);
			if (next !== components) commitComponents(normalizeComponents(next));
		},
		[commitComponents, componentsState, disabled]
	);

	const unwrapContainer = useCallback(
		(index: number) => {
			if (disabled) return;
			const components = normalizeComponents(componentsState);
			const next = unwrapContainerAt(components, index);
			if (next !== components) commitComponents(normalizeComponents(next));
		},
		[commitComponents, componentsState, disabled]
	);

	// Session undo/redo over this body's component list. resetKey (historyEpoch)
	// clears history when an external document replaces the value.
	const history = useComponentHistory({
		value: componentsState,
		apply: commitComponents,
		resetKey: String(historyEpoch)
	});

	const addComponentFromDefinition = useCallback(
		(definition: ComponentDefinition | null) => {
			if (!definition || disabled) return;
			if (contentViewMode === "markdown" && markdownInsertRef.current) {
				markdownInsertRef.current(definition);
				setShowLibrary(false);
				return;
			}
			const nextComponent = createComponentInstance(definition);
			const nextComponents = normalizeComponents([
				...componentsForPayload(previewComponents),
				nextComponent
			]);
			commitComponents(nextComponents);
			// Open the card we just appended, so adding a block lands you in it instead of leaving the
			// list collapsed with nothing selected.
			setActiveComponentKey(getComponentRuntimeKey(definition.type as string, nextComponents.length - 1));
			setShowLibrary(false);
		},
		[commitComponents, contentViewMode, disabled, previewComponents]
	);

	// Mode-switch + text-draft synchronization. The Markdown and JSON editors edit plain strings,
	// kept in separate state so a keystroke doesn't re-serialize the component tree on every
	// render. `lastMarkdownKeyRef`/`lastJsonKeyRef` fingerprint the components the current draft
	// was generated from: the draft is rebuilt only on entering the mode or when the committed
	// components no longer match that fingerprint (i.e. after a commit changed them : which also
	// canonicalizes the user's text through the round-trip). Leaving markdown mode parses the
	// current draft one last time synchronously, because the debounce below may still be pending
	// and a fast mode switch must not drop the final keystrokes; parse errors on exit are ignored
	// so a half-typed block can't wipe the components.
	useEffect(() => {
		const componentsKey = JSON.stringify(componentsForPayload(previewComponents));
		const enteringJson = contentViewMode === "json" && viewModeRef.current !== "json";
		const componentsChangedWhileJson = contentViewMode === "json" && componentsKey !== lastJsonKeyRef.current;
		if (contentViewMode === "json" && (enteringJson || componentsChangedWhileJson)) {
			const draft = buildDraft(previewComponents, label, helper);
			setPageJsonDraft(serializePagePayload(draft, DEFAULT_LANGUAGE));
			setPageJsonError(null);
			lastJsonKeyRef.current = componentsKey;
		}

		const enteringMarkdown = contentViewMode === "markdown" && viewModeRef.current !== "markdown";
		const componentsChanged = contentViewMode === "markdown" && componentsKey !== lastMarkdownKeyRef.current;
		if (contentViewMode === "markdown" && (enteringMarkdown || componentsChanged)) {
			const draft = buildDraft(previewComponents, label, helper);
			const md = pageToEditableMarkdown(draft, DEFAULT_LANGUAGE, { includeFrontmatter: false });
			markdownDraftRef.current = md;
			setMarkdownDraft(md);
			setMarkdownError(null);
			lastMarkdownKeyRef.current = componentsKey;
		}

		if (contentViewMode !== "markdown" && viewModeRef.current === "markdown") {
			if (markdownPreviewDebounceRef.current) {
				clearTimeout(markdownPreviewDebounceRef.current);
				markdownPreviewDebounceRef.current = null;
			}
			try {
				const nextDraft = editableMarkdownToPage(
					markdownDraftRef.current,
					buildDraft(previewComponents, label, helper),
					DEFAULT_LANGUAGE
				);
				commitComponents(normalizeComponents(nextDraft.components));
				setMarkdownError(null);
			} catch {
				// ignore parse errors when leaving
			}
		}

		viewModeRef.current = contentViewMode;
		return () => {
			if (markdownPreviewDebounceRef.current) {
				clearTimeout(markdownPreviewDebounceRef.current);
				markdownPreviewDebounceRef.current = null;
			}
		};
	}, [contentViewMode, commitComponents, helper, label, previewComponents]);

	// Markdown edits commit on a debounce: parsing + re-rendering the preview on every keystroke
	// is too heavy, and a half-typed ::: block would flash errors. The ref mirrors the draft so
	// the mode-switch effect above can flush the very latest text even if the timer hasn't fired.
	const handleMarkdownChange = useCallback(
		(nextValue: string) => {
			if (disabled) return;
			markdownDraftRef.current = nextValue;
			setMarkdownDraft(nextValue);
			if (markdownPreviewDebounceRef.current) {
				clearTimeout(markdownPreviewDebounceRef.current);
				markdownPreviewDebounceRef.current = null;
			}
			markdownPreviewDebounceRef.current = setTimeout(() => {
				markdownPreviewDebounceRef.current = null;
				try {
					const nextDraft = editableMarkdownToPage(
						nextValue,
						buildDraft(previewComponents, label, helper),
						DEFAULT_LANGUAGE
					);
					commitComponents(normalizeComponents(nextDraft.components));
					setMarkdownError(null);
				} catch (err) {
					setMarkdownError(err instanceof Error ? err.message : "Markdown invalid.");
				}
			}, MARKDOWN_PREVIEW_DEBOUNCE_MS);
		},
		[commitComponents, disabled, helper, label, previewComponents]
	);

	const handlePageJsonChange = useCallback(
		(nextValue: string) => {
			if (disabled) return;
			setPageJsonDraft(nextValue);
			try {
				const nextDraft = parsePagePayload(
					nextValue,
					buildDraft(previewComponents, label, helper),
					DEFAULT_LANGUAGE
				);
				commitComponents(normalizeComponents(nextDraft.components));
				setPageJsonError(null);
			} catch {
				setPageJsonError("JSON not valid.");
			}
		},
		[commitComponents, disabled, helper, label, previewComponents]
	);

	const handleFormatJson = useCallback(() => {
		try {
			const formatted = JSON.stringify(JSON.parse(pageJsonDraft), null, 2);
			handlePageJsonChange(formatted);
		} catch {
			setPageJsonError("JSON not valid.");
		}
	}, [pageJsonDraft, handlePageJsonChange]);

	const editorOptions = useMemo(
		() => ({
			minimap: { enabled: false },
			lineNumbers: "on" as const,
			folding: true,
			showFoldingControls: "always" as const,
			formatOnPaste: true,
			formatOnType: true,
			wordWrap: "on" as const,
			scrollBeyondLastLine: false,
			automaticLayout: true,
			tabSize: 2
		}),
		[]
	);

		const editorPanel =
			contentViewMode === "blocks" ? (
				<PageEditorComponentsPanel
					editable={!disabled}
					previewComponents={previewComponents}
					componentDefinitionMap={componentDefinitionMap}
					activeComponentKey={activeComponentKey}
					setActiveComponentKey={setActiveComponentKey}
					updateComponentValue={updateComponentValue}
					removeComponent={removeComponent}
					moveComponent={moveComponent}
					duplicateComponent={duplicateComponent}
					onWrapInContainer={wrapInContainer}
					onUnwrapContainer={unwrapContainer}
					onUndo={history.undo}
					onRedo={history.redo}
					canUndo={history.canUndo}
					canRedo={history.canRedo}
					historyEntries={history.entries}
					historyIndex={history.currentIndex}
					onJumpHistory={history.jumpTo}
					onOpenLibrary={() => setShowLibrary(true)}
				/>
			) : contentViewMode === "markdown" ? (
				<PageEditorMarkdownPanel
					editable={!disabled}
					value={markdownDraft}
					error={markdownError}
					onChange={handleMarkdownChange}
					editorOptions={editorOptions}
					editorHeight={450}
					componentDefinitionMap={componentDefinitionMap}
					pageTitle={label}
					pageDescription={helper}
					onOpenComponentLibrary={() => setShowLibrary(true)}
					insertComponentRef={markdownInsertRef}
				/>
			) : (
				<PageEditorJsonPanel
					editable={!disabled}
					pageJsonDraft={pageJsonDraft}
					pageJsonError={pageJsonError}
					onFormat={handleFormatJson}
					onChange={handlePageJsonChange}
					jsonEditorOptions={editorOptions}
					editorHeight={450}
				/>
			);

		return (
			<div className="space-y-4">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<div>
					<p className="text-xs uppercase tracking-[0.3em] text-slate-400">{label}</p>
					{helper && <p className="text-sm text-slate-600">{helper}</p>}
				</div>
				<div className="flex items-center gap-3">
					<label className="inline-flex cursor-pointer items-center gap-1.5 text-xs font-semibold text-slate-600">
						<input
							type="checkbox"
							checked={showPreview}
							onChange={(event) => setShowPreview(event.target.checked)}
						/>
						Aperçu en direct
					</label>
					<ModeButton active={contentViewMode === "blocks"} onClick={() => setContentViewMode("blocks")}>
						Blocks
					</ModeButton>
					<ModeButton active={contentViewMode === "markdown"} onClick={() => setContentViewMode("markdown")}>
						Markdown
					</ModeButton>
					<ModeButton active={contentViewMode === "json"} onClick={() => setContentViewMode("json")}>
						JSON
					</ModeButton>
				</div>
			</div>

			<div className={`grid gap-5 ${showPreview ? "md:grid-cols-2" : ""}`}>
				<div className="min-w-0">{editorPanel}</div>
				{showPreview && (
					<div className="min-w-0">
						<PageEditorPreviewPanel
							previewViewport={previewViewport}
							onViewportChange={setPreviewViewport}
							previewPageUrl=""
							productionPageUrl=""
							previewContainerRef={previewContainerRef}
							previewCss={previewCss}
							previewComponents={previewComponents}
							previewWidth={previewWidth}
							previewContainerWidth={previewContainerWidth}
							runtimeConfig={runtimeConfig}
							activeComponentIndex={contentViewMode === "blocks" ? activeComponentIndex : null}
							onSelectComponent={contentViewMode === "blocks" ? handleSelectComponentFromPreview : undefined}
							minHeight={450}
							contentHeight={450}
						/>
					</div>
				)}
			</div>

			<PageEditorComponentLibraryModal
				show={showLibrary}
				onClose={() => {
					setShowLibrary(false);
					libraryPicker.reset();
				}}
				picker={libraryPicker}
				onAdd={addComponentFromDefinition}
				runtimeConfig={runtimeConfig}
			/>
		</div>
	);
};

export default EmbeddedComponentsEditor;
